import ComboBox, {
  type ComboBoxInputItemType,
} from "@ui-kit/form/ComboBox/ComboBox.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Box from "@ui-kit/layout/Box/Box.web";
import useSearchItem from "./hooks/useSearchItem";
import type { SearchItemSharedProps } from "./SearchItem.props";

// Re-export for backwards compatibility
export type { SearchItemSharedProps } from "./SearchItem.props";

/**
 * Input for searching events, products, or membership groups.
 * Now uses the unified ComboBox component with portal support.
 */
export default function SearchItem<ItemType extends ComboBoxInputItemType>(
  props: SearchItemProps<ItemType>
): JSX.Element {
  const {
    listItemTemplate,
    single,
    multiple: _multiple,
    value: singleValue,
    ...rest
  } = props;
  const {
    items,
    handleSingleSelectItem,
    handleDropdownClickChange,
    handleInputChange,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    handleClearSearchTerm,
    label,
    inputValue,
    selectedSet,
    handleRemoveItem,
  } = useSearchItem(props);

  // Render item with template or default
  const renderItem = (item: ItemType) => {
    if (listItemTemplate) {
      return listItemTemplate(item, String(inputValue ?? ""));
    }
    return (
      <Box pl="xs" w="100%">
        <Text isTruncated p="2xs">
          {item.name}
        </Text>
      </Box>
    );
  };

  if (single) {
    // Single-select mode
    return (
      <ComboBox<ItemType>
        {...rest}
        id={rest.id ?? "search-item"}
        placeholder={rest.placeholder ?? label ?? "Search..."}
        items={items}
        value={singleValue}
        onChange={handleSingleSelectItem}
        onInputValueChange={handleInputChange}
        onClearClick={handleClearSearchTerm}
        isLoading={isFetching}
        hasNextPage={hasNextPage}
        onLoadMore={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        renderItem={renderItem}
      />
    );
  }

  // Multi-select mode
  return (
    <ComboBox<ItemType>
      {...rest}
      multiple
      id={rest.id ?? "search-item"}
      placeholder={rest.placeholder ?? label ?? "Search..."}
      label={label}
      items={items}
      value={selectedSet}
      onChange={() => {
        // This is called with the full array after add/remove
        // We don't need to do anything here since onItemAdd/onItemRemove handle the logic
      }}
      onItemAdd={handleDropdownClickChange}
      onItemRemove={handleRemoveItem}
      onInputValueChange={handleInputChange}
      onClearClick={handleClearSearchTerm}
      isLoading={isFetching}
      hasNextPage={hasNextPage}
      onLoadMore={fetchNextPage}
      renderItem={renderItem}
      excludeSelected
    />
  );
}

export type SearchItemProps<ItemType extends ComboBoxInputItemType> =
  SearchItemSharedProps<ItemType> & {
    // Add any web specific props here.
  };
